import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Heart, Coffee, Star, Award, Crown, Mail, Check, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const packageIcons = {
  coffee: Coffee,
  supporter: Heart,
  champion: Star,
  hero: Award,
  patron: Crown
};

export default function DonatePage() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/donate/packages`);
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDonate = async (packageId) => {
    setSelectedPackage(packageId);
    setProcessing(true);
    
    try {
      const response = await axios.post(`${API}/donate/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setProcessing(false);
      setSelectedPackage(null);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      toast.success('Thank you for subscribing!');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-20" data-testid="donate-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="p-4 bg-red-500/20 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
            <Heart className="h-10 w-10 text-red-400" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
            Support Independent<br />Journalism
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            The Watchtower is funded entirely by readers like you. Your donation helps 
            us investigate political misconduct and hold power accountable.
          </p>
        </div>
      </section>

      {/* Donation Packages */}
      <section className="py-16 -mt-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              [1, 2, 3, 4, 5].map(i => (
                <div key={i} className="skeleton h-48 rounded-none"></div>
              ))
            ) : (
              packages.map((pkg) => {
                const Icon = packageIcons[pkg.id] || Heart;
                const isSelected = selectedPackage === pkg.id;
                
                return (
                  <div 
                    key={pkg.id}
                    className={`bg-white border-2 p-6 transition-all ${
                      isSelected ? 'border-slate-900 shadow-lg' : 'border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`donate-package-${pkg.id}`}
                  >
                    <Icon className="h-8 w-8 mb-4 text-slate-700" />
                    <h3 className="font-serif text-xl font-medium mb-1">{pkg.name}</h3>
                    <p className="text-3xl font-mono font-bold mb-2">£{pkg.amount}</p>
                    <p className="text-sm text-slate-500 mb-6">{pkg.description}</p>
                    <Button 
                      onClick={() => handleDonate(pkg.id)}
                      disabled={processing}
                      className="w-full btn-primary"
                      data-testid={`donate-btn-${pkg.id}`}
                    >
                      {isSelected && processing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Donate'
                      )}
                    </Button>
                  </div>
                );
              })
            )}
          </div>

          {/* PayPal Alternative */}
          <div className="mt-12 text-center">
            <p className="text-slate-500 text-sm mb-4">Prefer PayPal?</p>
            <a 
              href="https://paypal.me/thewatchdog" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block btn-secondary"
            >
              Donate via PayPal
            </a>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mail className="h-10 w-10 mx-auto mb-4 text-slate-600" />
          <h2 className="font-serif text-2xl md:text-3xl font-medium mb-4">
            Stay Informed
          </h2>
          <p className="text-slate-600 mb-8">
            Subscribe to our newsletter for weekly updates on political accountability 
            and the latest scandal reports.
          </p>
          
          {subscribed ? (
            <div className="flex items-center justify-center space-x-2 text-green-600">
              <Check className="h-5 w-5" />
              <span>You're subscribed! Check your inbox.</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 rounded-none border-slate-300"
                required
                data-testid="newsletter-email"
              />
              <Button 
                type="submit" 
                disabled={subscribing}
                className="btn-primary"
                data-testid="newsletter-submit"
              >
                {subscribing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Why Donate */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl md:text-3xl font-medium text-center mb-12">
            Where Your Money Goes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center mx-auto mb-4 text-2xl font-mono font-bold">
                60%
              </div>
              <h3 className="font-medium mb-2">Investigation</h3>
              <p className="text-sm text-slate-500">
                Funding research, FOI requests, and in-depth investigative journalism.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center mx-auto mb-4 text-2xl font-mono font-bold">
                25%
              </div>
              <h3 className="font-medium mb-2">Technology</h3>
              <p className="text-sm text-slate-500">
                Maintaining servers, security, and developing new features.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center mx-auto mb-4 text-2xl font-mono font-bold">
                15%
              </div>
              <h3 className="font-medium mb-2">Outreach</h3>
              <p className="text-sm text-slate-500">
                Spreading awareness and protecting whistleblower security.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
